import { Injectable, Injector } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Router,
  RouterStateSnapshot,
} from '@angular/router';
import { Observable, of, merge, Subject } from 'rxjs';
import { catchError, take, map } from 'rxjs/operators';
import { Actions, ofType } from '@ngrx/effects';
import { Destroyer, logger } from '@shared/utils';

@Injectable({
  providedIn: 'root',
})
export class StoresBaseGuard extends Destroyer implements CanActivate {
  name: string;
  namePrefix = '';
  onErrorPage = '/';
  successAction: any;
  errorAction: any;

  route: ActivatedRouteSnapshot;
  state: RouterStateSnapshot;
  router: Router;

  private emitter$: Subject<boolean> = new Subject<boolean>();
  private actions$: Actions;

  private get successAction$() {
    return this.actions$.pipe(ofType(this.successAction));
  }

  private get errorAction$() {
    return this.actions$.pipe(ofType(this.errorAction));
  }

  private get manualAction$() {
    return this.emitter$.asObservable();
  }

  constructor(private injector: Injector) {
    super();
    this.actions$ = this.injector.get<Actions>(Actions);
    this.router = this.injector.get<Router>(Router);
  }

  async beforeDispatcher() {}
  dispatcher() {}
  onSuccess(data: any) {}
  onError(err: any) {
    if (this.onErrorPage) {
      this.router.navigateByUrl(this.onErrorPage);
    }
  }

  setStatusManually(success: boolean) {
    setTimeout(() => this.emitter$.next(success));
  }

  async canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Promise<boolean> {
    this.route = route;
    this.state = state;

    await this.beforeDispatcher();

    logger('group', this.getGuardName());

    this.dispatcher();

    return merge(
      this.successAction$,
      this.errorAction$,
      this.manualAction$
    ).pipe(
      take(1),
      map((e) => this.checkIsSuccess(e)),
      catchError((e) => {
        logger('failed', `Guard error`, e);
        logger('groupEnd');
        return of(false);
      })
    ).toPromise();
  }

  private checkIsSuccess(item): boolean {
    let result = typeof item === 'boolean' ? item : undefined;

    if (result === undefined) {
      const successKey = this.successAction.type;
      const currentKey = item && item.type;
      result = currentKey === successKey;
    }

    if (result === true) {
      logger('success', `Guard accepted`);
      this.onSuccess(item);
    } else {
      logger('failed', `Guard declined`, item);
      this.onError(item);
    }
    logger('groupEnd');
    logger('groupEnd');
    return result;
  }

  private getGuardName(): string {
    const key = this.name || this.successAction.type || 'no name';
    const name = key.replace('[', '').split(']')[0];
    return `Guard ${this.namePrefix}${name}`;
  }
}
